import { useQuery, UseQueryResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { Map } from "../../models/Models";
import {
    EpgEvent,
    EpgEventDetailOverride,
    EpgReport,
    PlanbyChannel,
    PlanbyEventCollection,
    VodContentReport,
} from "../../models/epg/Models";
import { isQueryError, QueryError, QueryResult } from "../../utils/Query";
import { queryRequest } from "../../utils/QueryAxios";
import { isPromiseFulfilled, isPromiseRejected } from "../../utils/PromiseUtil";

export const usePlanbyChannels = (): UseQueryResult<PlanbyChannel[]> =>
    useQuery({
        queryKey: [ "planbyChannels" ],
        queryFn: async (): Promise<PlanbyChannel[]> => {
            const queryResult: QueryResult<PlanbyChannel[]> | QueryError<PlanbyChannel[]> =
                await queryRequest("/epg/channel/view/planby") as QueryResult<PlanbyChannel[]> | QueryError<PlanbyChannel[]>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data ? queryResult.data : [];
        },
    });

export const usePlanbyEventCollectionByDateByChannelTag = (channelTags: string[] | undefined): UseQueryResult<Map<PlanbyEventCollection[]>> =>
    useQuery({
        enabled: !!channelTags,
        queryKey: [ "planbyEventCollectionByDateByChannelTag", channelTags ],
        queryFn: async (): Promise<Map<PlanbyEventCollection[]>> => {
            const queryResultPromises: Promise<QueryResult<PlanbyEventCollection[]> | QueryError<PlanbyEventCollection[]>>[] = [];

            channelTags?.forEach(
                (channelTag: string) => {
                    if (channelTag) {
                        queryResultPromises.push(
                            queryRequest(`/epg/channel/${channelTag}/event/view/planby`) as Promise<QueryResult<PlanbyEventCollection[]> | QueryError<PlanbyEventCollection[]>>
                        );
                    }
                }
            );

            return Promise.allSettled(queryResultPromises).then(
                (results: PromiseSettledResult<QueryResult<PlanbyEventCollection[]> | QueryError<PlanbyEventCollection[]>>[]) => {
                    const eventCollectionByDateByChannelTag: Map<PlanbyEventCollection[]> = {};

                    try {
                        results?.forEach((result: PromiseSettledResult<QueryResult<PlanbyEventCollection[]> | QueryError<PlanbyEventCollection[]>>) => {
                            try {
                                if (isPromiseFulfilled(result)) {
                                    const queryResult: QueryResult<PlanbyEventCollection[]> | QueryError<PlanbyEventCollection[]> = result?.value;
                                    if (isQueryError(queryResult)) {
                                        console.error(`Query Error: HTTP ${queryResult.status} - ${queryResult.statusText}`);
                                    } else if (queryResult?.data?.length) {
                                        const firstEventCollection = queryResult.data.find((eventCollection) => eventCollection?.events?.length);
                                        if (firstEventCollection?.events?.length && !eventCollectionByDateByChannelTag[firstEventCollection.events[0].channelUuid]) {
                                            eventCollectionByDateByChannelTag[firstEventCollection.events[0].channelUuid] = queryResult.data;
                                        }
                                    }
                                } else if (isPromiseRejected(result)) {
                                    console.error(`Promise rejected: ${result.reason}`);
                                }
                            } catch (e) {
                                console.error(e);
                            }
                        });
                    } catch (e) {
                        console.error(e);
                    }
                    return eventCollectionByDateByChannelTag;
                }
            );
        },
    });

export const useEpgEventDetailOverrideById = (id: string): UseQueryResult<EpgEvent | undefined> => {
    return useQuery({
        queryKey: [ "epgEventDetailOverride", id ],
        queryFn: async (): Promise<EpgEvent | undefined> => {
            const queryResult: QueryResult<EpgEvent> | QueryError<EpgEvent> =
                await queryRequest(`/epg/event/${id}`) as QueryResult<EpgEvent> | QueryError<EpgEvent>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
    });
};

export const useUpdateEpgEventDetailOverride = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data : {id: string; epgEventDetailOverride: EpgEventDetailOverride}): Promise<unknown> => {
            const queryResult =
                    await queryRequest({ url: `/epg/event/`, method:"PUT", data: data.epgEventDetailOverride });

            if (isQueryError(queryResult)) {
                    throw new Error(queryResult.statusText);
            }

            return queryResult.data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [ "epgEventDetailOverride", variables.id ] });
        },
    });
};

export const useEventIngestTrigger = () => {
    return useMutation({
        mutationFn: async () : Promise<void> => {
            const queryResult =
                await queryRequest({ url: `/epg/event-ingest/trigger`, method: `POST` });

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }
        },
    });
};

export const useEventIngestChannelTrigger = () => {
    return useMutation({
        mutationFn: async (pipelineName: string) : Promise<void> => {
            const queryResult =
                await queryRequest({ url:`/epg/event-ingest/${pipelineName}/trigger`, method: `POST` });

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }
        },
    });
};

export const useEventIngestMetadataUpload = () => {
    return useMutation({
        mutationFn: async ({ pipelineName, file }: {pipelineName: string, file: File}): Promise<void> => {
            const formData = new FormData();
            formData.append("file", file);
            const queryResult =
                await queryRequest({ url: `/epg/event-ingest/${pipelineName}/upload`, method: `POST`, data: formData });

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }
        },
    });
};

export const useEventIngestImageUpload = () => {
    return useMutation({
        mutationFn: async ({ channelUuid, file }: {channelUuid: string, file: File}): Promise<void> => {
            const formData = new FormData();
            formData.append("file", file);

            const queryResult =
                await queryRequest({
                    url: `/epg/event-image-ingest/upload-event-epg-event-images-library/upload?channel-tag=${channelUuid}`,
                    method: `POST`,
                    data: formData,
                });

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }
        },
    });
};

export const useChannelTagsWithPipelineNames = () => useQuery({
    queryKey: [ "channelTagsWithPipelineNames" ],
    queryFn: async (): Promise<Record<string, string>> => {
        const queryResult: QueryResult<Record<string, string>> | QueryError<Record<string, string>> =
            await queryRequest("/epg/channel-tags-with-pipeline-names") as QueryResult<Record<string, string>> | QueryError<Record<string, string>>;

        if (isQueryError(queryResult)) {
            throw new Error(queryResult.statusText);
        }

        return queryResult?.data ? queryResult.data : {};
    },
});

export const useEpgCoverage = (): UseQueryResult<EpgReport | undefined> =>
    useQuery({
        queryKey: [ "epgEventReport" ],
        queryFn: async (): Promise<EpgReport | undefined> => {
            const  queryResult: QueryResult<EpgReport> | QueryError<EpgReport> =
                await queryRequest("/epg/event/report") as QueryResult<EpgReport> | QueryError<EpgReport>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
    });

export const useVodContentReport = (): UseQueryResult<VodContentReport | undefined> =>
    useQuery({
        queryKey: [ "vodContentReport" ],
        queryFn: async (): Promise<VodContentReport | undefined> => {
            const queryResult: QueryResult<VodContentReport> | QueryError<VodContentReport> =
                await queryRequest("/vod/content/report") as QueryResult<VodContentReport>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        select: (data) => {
            if (!data)
                return data;

            return {
                ...data,
                imagesTotal: Object.values(data.contentImagesReport).reduce((prev, curr) => prev + curr, 0),
            };
        },
    });